// QR Kiosk v2 — KV2-9 the escalation ladder. ONE slot, at the TOP (§6.1):
//   NameplateEscalation — the D9 TOP slot (caption + Sign in / Guest code), above the plate.
//   EscalationPill      — the pill itself; also reused by the 404 state (NameplateUnavailable).
//
// The ghost ladder that used to close the page (three dashed locked rows: service history,
// manuals & documents, report work) is GONE — owner ruling D15, 2026-08-14: "keep just the
// plate and the login options", nothing renders below the plate. Do not re-add the rows, and
// do not re-home them above the plate: the pills ARE the escalation surface now.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import accountContext from "../../../account/accountContext";
import { buildAccountSignInWithReturn } from "../../../account/routes";
import useTranslation from "../../../shared/hooks/useTranslation";
import { menuPath } from "../shared/kioskNav";

export interface NameplateEscalationProps {
  /** machineNo (InternalNumber) — the kiosk routes are keyed on it. Empty → slot collapses. */
  machineNo: string | null;
}

export interface EscalationPillProps {
  label: string;
  /** "slate" = the Sign-in weight (accent wash + accent border). "plain" = card + hairline. */
  tone: "slate" | "plain";
  onClick: () => void;
}

/** The escalation pill. The DRAWN pill is 34px tall per the artboard; the <button> pads out
 *  to a 44px tap target with TRANSPARENT padding, so the visual weight matches the canvas
 *  while the hit area still clears the ≥44px rule (build spec §6.2 / §8). Never give the
 *  button itself a background — that is what turned this row into a slab pair. */
export function EscalationPill({ label, tone, onClick }: EscalationPillProps) {
  const [focused, setFocused] = useState(false);
  const slate = tone === "slate";

  return (
    <button
      type="button"
      onClick={onClick}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        height: 44,
        padding: 0,
        margin: 0,
        border: 0,
        background: "transparent",
        fontFamily: "inherit",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          height: 34,
          padding: "0 15px",
          borderRadius: 999,
          background: slate ? "var(--tier-accent-soft)" : "var(--card)",
          border: `1px solid ${slate ? "var(--tier-accent)" : "var(--border)"}`,
          color: slate ? "var(--tier-accent)" : "var(--ink-2)",
          fontSize: 12.5,
          lineHeight: "16px",
          fontWeight: slate ? 600 : 500,
          whiteSpace: "nowrap",
          boxShadow: focused ? "0 0 0 3px var(--tier-accent-line)" : "none",
          transition: "box-shadow .12s",
        }}
      >
        {label}
      </span>
    </button>
  );
}

// D9 (owner, 2026-08-11): these are PILLS at the TOP, deliberately light-weight. Do NOT "improve" them into full-width primary buttons — Tier 0 is anonymous-first, and a heavy top slot turns an identity page into a login wall. Artboard: "N2 · Phone · plate ON · access top B (pills)".
export function NameplateEscalation({ machineNo }: NameplateEscalationProps) {
  const { translate } = useTranslation("public");
  const navigate = useNavigate();

  // §6.2 / §6.4: the whole ladder is keyed on machineNo being on the anonymous wire
  // (Security S4 INCLUDE-WITH-MITIGATION, over-exposure pinned to the still-open B2/D5
  // ruling). Degrade to ABSENT, never to a broken link. If B2 later strips machineNo from
  // the anonymous projection, re-wire these onto an authenticated code→machine resolve —
  // proposed as D7-BE1 in B7_D7_D8_ONE_CODE_DEFAULT_ON_PLAN.md §3.3.
  const number = (machineNo || "").trim();
  if (!number) return null;

  const target = menuPath(number);

  const goSignIn = () => {
    // "authenticated already" — the same read KioskEntryGate makes.
    if (accountContext.user) {
      navigate(target);
      return;
    }
    navigate(buildAccountSignInWithReturn(target));
  };

  // Lands on KioskEntryGate → unauthenticated → GuestEntryV2, opened straight in PIN mode
  // with the machine locked from the URL (the ?entry=pin support added in §6.3).
  const goGuest = () => navigate(`${target}?entry=pin`);

  return (
    // ONE row: the caption carries the meaning, the pills are the quiet door. The pills sit
    // in their own flex child so a narrow / long-locale line breaks caption-above-pills
    // rather than orphaning "Guest code" onto a row of its own.
    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, minWidth: 0 }}>
      <span
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          fontSize: 11.5,
          lineHeight: "14px",
          color: "var(--muted)",
        }}
      >
        {translate("kioskV2NameplateAccessPrompt")}
      </span>
      <span style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <EscalationPill tone="slate" label={translate("kioskV2NameplateSignIn")} onClick={goSignIn} />
        <EscalationPill tone="plain" label={translate("kioskV2NameplateGuestCode")} onClick={goGuest} />
      </span>
    </div>
  );
}

// HISTORY, so the rulings are not re-litigated from an empty file:
//   D10 (owner, 2026-08-11) — no "Ask MIDAS" row: MIDAS is hidden from guests (2026-08-04
//     pivot), so advertising it to an anonymous scanner promised what a redeemed guest
//     cannot reach.
//   D14 (owner, 2026-08-13) — documents went login-only, and the "Manuals & documents"
//     section moved off the plate into a third locked ghost row.
//   D15 (owner, 2026-08-14) — the whole ghost ladder is cut. The page is chrome + access
//     pills + the plate; nothing renders below the plate. The three ghost-row strings
//     (kioskV2NameplateGhostHistory / kioskV2NameplateDocuments / kioskV2NameplateGhostReport)
//     were deleted from en/sk/cs public.json in the same commit.
